import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import AdminHeader from "../../components/header";
import AdminSidebar from "../../components/sidebar";
import styles from "../../styles/admin.module.css";
import axiosInstance from "../../../AxiosInstance";
import { formatTimestamp } from "../../components/utils";
import Table from "../../components/table/index";
import {
  faArrowLeft,
  faUser,
  faEnvelope,
  faPhone,
  faMapMarkerAlt,
  faCalendarAlt,
  faClock,
  faStar,
  faShoppingCart,
  faChartLine,
  faIdCard,
  faBuilding,
  faList,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { formatPrice } from "../../../utils/utils";

export default function ClientDetail() {
  const { userId } = useParams();
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cart, setCart] = useState(null);
  const [cartLoading, setCartLoading] = useState(true);

  useEffect(() => {
    // Charger les données du user
    setLoading(true);
    axiosInstance.get(`clients/${userId}/`).then((response) => {
      setUserData(response.data);
      setLoading(false);
    });

    // Charger le panier
    setCartLoading(true);
    axiosInstance
      .get(`clients/${userId}/cart/`)
      .then((response) => {
        setCart(response.data);
      })
      .catch((err) => {
        setCart(null);
      })
      .finally(() => {
        setCartLoading(false);
      });
  }, [userId]);

  const getStatusBadge = () => {
    if (userData?.is_archived) {
      return (
        <span className="badge bg-secondary px-3 py-2 rounded-pill">
          <i className="fas fa-archive me-1"></i>
          Archivé
        </span>
      );
    } else if (userData?.is_blocked) {
      return (
        <span className="badge bg-danger px-3 py-2 rounded-pill">
          <i className="fas fa-ban me-1"></i>
          Bloqué
        </span>
      );
    } else {
      return (
        <span className="badge bg-success px-3 py-2 rounded-pill">
          <i className="fas fa-check-circle me-1"></i>
          Actif
        </span>
      );
    }
  };

  const getActivityLevel = () => {
    const orders = userData?.user_sales || 0;
    if (orders === 0)
      return { level: "Nouveau", color: "text-muted", icon: "fa-user-plus" };
    if (orders <= 5)
      return { level: "Occasionnel", color: "text-info", icon: "fa-user" };
    if (orders <= 20)
      return {
        level: "Régulier",
        color: "text-primary",
        icon: "fa-user-check",
      };
    return { level: "Fidèle", color: "text-success", icon: "fa-crown" };
  };

  // Configuration des colonnes pour le tableau des commandes
  const orderColumns = [
    {
      title: "Référence",
      internal: "reference",
      sortable: true,
      sortField: "reference",
      filterable: true,
      searchField: "reference",
    },
    {
      title: "Date",
      internal: "date",
      sortable: true,
      sortField: "date",
      filterable: true,
      searchField: "date",
      code: (item, value) => formatTimestamp(new Date(value)),
    },
    {
      title: "Statut",
      internal: "status",
      sortable: true,
      sortField: "status",
      filterable: true,
      searchField: "status",
      code: (item, value) => {
        const statusMap = {
          0: { label: "En attente", color: "warning" },
          1: { label: "En préparation", color: "info" },
          2: { label: "En livraison", color: "primary" },
          3: { label: "Livré", color: "success" },
          4: { label: "Terminé", color: "success" },
          5: { label: "Annulé", color: "danger" },
          6: { label: "Annulé", color: "danger" },
        };
        const status = statusMap[value] || {
          label: "Inconnu",
          color: "secondary",
        };

        return (
          <div>
            <span className={`badge bg-${status.color} px-2 py-1`}>
              {status.label}
            </span>
            {/* Afficher les informations d'annulation si la commande est annulée */}
            {(value === 5 || value === 6) &&
              (item.cancellation_reason || item.cancellation_detail) && (
                <div className="mt-1">
                  {item.cancellation_reason && (
                    <div className="small text-danger">
                      <strong>Raison:</strong> {item.cancellation_reason}
                    </div>
                  )}
                  {item.cancellation_detail && (
                    <div className="small text-muted">
                      <strong>Détail:</strong> {item.cancellation_detail}
                    </div>
                  )}
                </div>
              )}
          </div>
        );
      },
    },
    {
      title: "Sous-total",
      internal: "sub_total",
      sortable: true,
      sortField: "sub_total",
      code: (item, value) => formatPrice(value),
    },
    {
      title: "Frais de livraison",
      internal: "fee",
      sortable: true,
      sortField: "fee",
      code: (item, value) => formatPrice(value),
    },
    {
      title: "Total",
      internal: "total",
      sortable: false,
      code: (item) => formatPrice(item.sub_total + item.fee),
    },
    {
      title: "Mode de paiement",
      internal: "paiment_mode",
      sortable: true,
      sortField: "paiment_mode",
      filterable: true,
      searchField: "payment_mode",
      code: (item, value) => {
        const paymentMap = {
          cash: "Espèces",
          wave: "Wave",
          "Orange Money": "Orange Money",
          maxit: "Maxit",
        };
        return paymentMap[value] || value;
      },
    },
    {
      title: "Points utilisés",
      internal: "points_used",
      sortable: true,
      sortField: "points_used",
    },
    {
      title: "Points gagnés",
      internal: "points_earned",
      sortable: true,
      sortField: "points_earned",
    },
    {
      title: "Adresse",
      internal: "address",
      sortable: false,
      filterable: true,
      searchField: "address",
    },
  ];

  // Log pour déboguer l'URL de l'API
  console.log("ClientDetail - userId:", userId);
  console.log("ClientDetail - API URL:", `clients/${userId}/orders`);

  if (loading) {
    return (
      <div className={styles.mainContainer}>
        <AdminHeader />
        <AdminSidebar />
        <div className={`${styles.container} me-4`}>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <div className="text-center">
              <div
                className="spinner-border text-primary"
                role="status"
                style={{ width: "3rem", height: "3rem" }}
              >
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-3 text-muted">
                Chargement des informations client...
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const activityLevel = getActivityLevel();

  return (
    <div className={styles.mainContainer}>
      <AdminHeader />
      <AdminSidebar />

      <div className={`${styles.container} me-4`}>
        {/* Header avec navigation et statut */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div className="d-flex align-items-center">
            <Link
              to="/admin/clients"
              className="btn btn-outline-secondary me-3 d-flex align-items-center"
              style={{ borderRadius: "50px", padding: "8px 16px" }}
            >
              <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
              Retour
            </Link>
            <div>
              <h2 className="mb-1" style={{ fontWeight: "600" }}>
                {userData?.name} {userData?.lastname}
              </h2>
              <p className="text-muted mb-0">
                <FontAwesomeIcon icon={faIdCard} className="me-1" />
                ID: {userData?.id}
              </p>
            </div>
          </div>
          <div className="d-flex align-items-center gap-3">
            <div className="text-end">
              <div className="text-muted small">Niveau d'activité</div>
              <div className={`fw-bold ${activityLevel.color}`}>
                <i className={`fas ${activityLevel.icon} me-1`}></i>
                {activityLevel.level}
              </div>
            </div>
          </div>
        </div>

        {/* Statistiques rapides */}
        <div className="row mb-4">
          <div className="col-md-3 mb-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                background:
                  "linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.15)), linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              }}
            >
              <div className="card-body text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">{userData?.user_sales || 0}</h3>
                    <p className="mb-0 small opacity-75">Commandes</p>
                  </div>
                  <FontAwesomeIcon
                    icon={faShoppingCart}
                    size="lg"
                    className="opacity-75"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                background:
                  "linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.15)), linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
              }}
            >
              <div className="card-body text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">{userData?.points || 0}</h3>
                    <p className="mb-0 small opacity-75">Points</p>
                  </div>
                  <FontAwesomeIcon
                    icon={faStar}
                    size="lg"
                    className="opacity-75"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                background:
                  "linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.15)), linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
              }}
            >
              <div className="card-body text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">
                      {userData?.last_order_timestamp ? "Oui" : "Non"}
                    </h3>
                    <p className="mb-0 small opacity-75">
                      A commandé récemment
                    </p>
                  </div>
                  <FontAwesomeIcon
                    icon={faChartLine}
                    size="lg"
                    className="opacity-75"
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3 mb-3">
            <div
              className="card border-0 shadow-sm h-100"
              style={{
                background:
                  "linear-gradient(rgba(0,0,0,0.15), rgba(0,0,0,0.15)), linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
              }}
            >
              <div className="card-body text-white">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">
                      {userData?.last_login ? "Oui" : "Non"}
                    </h3>
                    <p className="mb-0 small opacity-75">Connecté récemment</p>
                  </div>
                  <FontAwesomeIcon
                    icon={faClock}
                    size="lg"
                    className="opacity-75"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Informations détaillées */}
        <div className="row">
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={faUser}
                    className="me-2 text-primary"
                  />
                  Informations Personnelles
                </h5>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faUser} className="me-2" />
                      Nom complet
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold">
                      {userData?.name} {userData?.lastname}
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faEnvelope} className="me-2" />
                      Email
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold text-primary">
                      {userData?.email}
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faPhone} className="me-2" />
                      Téléphone
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold">{userData?.phone_number}</div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2" />
                      Adresse
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold">
                      {userData?.address || "Non renseignée"}
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faBuilding} className="me-2" />
                      District
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold">
                      {userData?.district || "Non renseigné"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={faIdCard}
                    className="me-2 text-primary"
                  />
                  Informations du Compte
                </h5>
              </div>
              <div className="card-body">
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                      Date de création
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold">
                      {userData?.date_joined
                        ? formatTimestamp(new Date(userData.date_joined))
                        : "N/A"}
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faClock} className="me-2" />
                      Dernière connexion
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold">
                      {userData?.last_login
                        ? formatTimestamp(new Date(userData.last_login))
                        : "Jamais connecté"}
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faStar} className="me-2" />
                      Points fidélité
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold text-warning">
                      {userData?.points || 0} points
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faShoppingCart} className="me-2" />
                      Total commandes
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold text-success">
                      {userData?.user_sales || 0} commandes
                    </div>
                  </div>
                </div>
                <hr className="my-3" />
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <div className="d-flex align-items-center text-muted small">
                      <FontAwesomeIcon icon={faChartLine} className="me-2" />
                      Dernière commande
                    </div>
                  </div>
                  <div className="col-sm-8">
                    <div className="fw-semibold">
                      {userData?.last_order_timestamp
                        ? formatTimestamp(
                            new Date(userData.last_order_timestamp)
                          )
                        : "Aucune commande"}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Panier en cours */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={faShoppingCart}
                    className="me-2 text-primary"
                  />
                  Panier en cours
                </h5>
              </div>
              <div className="card-body">
                {cartLoading ? (
                  <div className="text-center py-5">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="mt-2 text-muted">Chargement du panier...</p>
                  </div>
                ) : cart && cart.items && cart.items.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table align-middle">
                      <thead>
                        <tr>
                          <th>Produit</th>
                          <th className="text-center">Quantité</th>
                          <th className="text-end">Prix unitaire</th>
                          <th className="text-end">Sous-total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {cart.items.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <div className="d-flex align-items-center">
                                <div>
                                  <div className="fw-semibold">
                                    {item.product.name}
                                  </div>
                                  <div className="text-muted small">
                                    {item.product.category?.name}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="text-center">{item.qty}</td>
                            <td className="text-end">
                              {formatPrice(item.product.price)}
                            </td>
                            <td className="text-end fw-bold">
                              {formatPrice(item.qty * item.product.price)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr>
                          <td colSpan="3" className="text-end fw-bold border-0">
                            Total du panier
                          </td>
                          <td className="text-end fw-bold h5 mb-0 border-0">
                            {formatPrice(
                              cart.items.reduce(
                                (total, item) =>
                                  total +
                                  item.qty * parseFloat(item.product.price),
                                0
                              )
                            )}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-5">
                    <FontAwesomeIcon
                      icon={faShoppingCart}
                      size="3x"
                      className="text-muted mb-3"
                    />
                    <h5 className="text-muted">
                      Le panier de cet utilisateur est vide.
                    </h5>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Liste des commandes */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-0 py-3">
                <h5 className="mb-0 d-flex align-items-center">
                  <FontAwesomeIcon
                    icon={faList}
                    className="me-2 text-primary"
                  />
                  Historique des commandes
                </h5>
              </div>
              <div className="card-body p-0">
                <Table
                  apiUrl={`clients/${userId}/orders`}
                  columns={orderColumns}
                  queryKey={`client-orders${userId}`}
                  title=""
                  itemsPerPageOptions={[10, 25, 50]}
                  urlPrefix={`client-orders-${userId}-`}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
